import { useState, useEffect } from "react";
import api from "../lib/api";
import { Loader2, ArrowDownCircle, ArrowUpCircle, RotateCcw, TrendingUp, Coins } from "lucide-react";

const TXN_META = {
  recharge: { label: "Recharge", color: "#10B981", icon: ArrowUpCircle, sign: "+" },
  credit: { label: "Recharge", color: "#10B981", icon: ArrowUpCircle, sign: "+" }, // legacy alias
  debit: { label: "Commission", color: "#EF4444", icon: ArrowDownCircle, sign: "-" },
  withdraw: { label: "Retrait admin", color: "#F59E0B", icon: ArrowDownCircle, sign: "-" },
  refund: { label: "Remboursement", color: "#3B82F6", icon: RotateCcw, sign: "+" },
  earning: { label: "Revenu net", color: "#10B981", icon: TrendingUp, sign: "+" },
};

function formatDate(iso) {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    return d.toLocaleString("fr-FR", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" });
  } catch {
    return iso;
  }
}

export default function TFTHistory({ endpoint = "/driver/tft/history", currentBalance = null, title = "Historique TFT" }) {
  const [txns, setTxns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filter, setFilter] = useState("all");

  useEffect(() => {
    let active = true;
    setLoading(true);
    setError(null);
    api.get(endpoint)
      .then(({ data }) => { if (active) setTxns(data || []); })
      .catch((e) => { if (active) setError(e.response?.data?.detail || "Erreur lors du chargement de l'historique"); })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [endpoint]);

  const filtered = filter === "all" ? txns : txns.filter((t) => t.type === filter);

  const totals = txns.reduce((acc, t) => {
    const meta = TXN_META[t.type];
    const sign = meta?.sign;
    // Exclude 'earning' from credits total since earnings track net revenue, not balance changes
    if (sign === "+" && t.type !== "earning") acc.credits += t.amount || 0;
    if (sign === "-") acc.debits += t.amount || 0;
    return acc;
  }, { credits: 0, debits: 0 });

  return (
    <div className="space-y-4" data-testid="tft-history-container">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <h2 className="text-lg font-semibold text-[#0A2540]" style={{ fontFamily: 'Outfit' }}>{title}</h2>
        <div className="flex gap-2 overflow-x-auto" data-testid="tft-history-filters">
          {[
            { key: "all", label: "Tous" },
            { key: "recharge", label: "Recharges" },
            { key: "debit", label: "Commissions" },
            { key: "refund", label: "Remboursements" },
            { key: "earning", label: "Revenus" },
            { key: "withdraw", label: "Retraits" },
          ].map((f) => (
            <button
              key={f.key}
              onClick={() => setFilter(f.key)}
              className={`px-3 py-1.5 text-xs font-semibold rounded-full whitespace-nowrap border transition-colors ${filter === f.key ? "bg-[#0A2540] text-white border-[#0A2540]" : "bg-white text-[#64748B] border-[#CBD5E1] hover:border-[#0A2540]"}`}
              data-testid={`tft-filter-${f.key}`}
            >
              {f.label}
            </button>
          ))}
        </div>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
        {currentBalance !== null && (
          <div className="bg-white border border-[#CBD5E1] rounded-xl p-3" data-testid="tft-summary-balance">
            <p className="text-[10px] uppercase tracking-wide text-[#64748B] mb-1">Solde actuel</p>
            <p className={`text-xl font-bold ${currentBalance > 0 ? "text-[#10B981]" : "text-[#EF4444]"}`} style={{ fontFamily: 'Outfit' }}>
              {currentBalance.toLocaleString()} TFT
            </p>
          </div>
        )}
        <div className="bg-white border border-[#CBD5E1] rounded-xl p-3" data-testid="tft-summary-credits">
          <p className="text-[10px] uppercase tracking-wide text-[#64748B] mb-1">Total recharges</p>
          <p className="text-xl font-bold text-[#10B981]" style={{ fontFamily: 'Outfit' }}>+{totals.credits.toLocaleString()}</p>
        </div>
        <div className="bg-white border border-[#CBD5E1] rounded-xl p-3" data-testid="tft-summary-debits">
          <p className="text-[10px] uppercase tracking-wide text-[#64748B] mb-1">Total débits</p>
          <p className="text-xl font-bold text-[#EF4444]" style={{ fontFamily: 'Outfit' }}>-{totals.debits.toLocaleString()}</p>
        </div>
      </div>

      <div className="bg-white border border-[#CBD5E1] rounded-xl overflow-hidden" data-testid="tft-history-list">
        {loading ? (
          <div className="py-12 flex items-center justify-center text-[#64748B]">
            <Loader2 className="w-5 h-5 animate-spin mr-2" /> Chargement...
          </div>
        ) : error ? (
          <p className="text-center py-10 text-[#EF4444] text-sm">{error}</p>
        ) : filtered.length === 0 ? (
          <div className="text-center py-12 text-[#64748B]">
            <Coins className="w-8 h-8 mx-auto mb-2 opacity-40" />
            <p className="text-sm">Aucun mouvement TFT pour ce filtre</p>
          </div>
        ) : (
          <ul className="divide-y divide-[#E2E8F0]">
            {filtered.map((t) => {
              const meta = TXN_META[t.type] || { label: t.type, color: "#64748B", icon: Coins, sign: "" };
              const Icon = meta.icon;
              return (
                <li key={t.id} className="flex items-center justify-between gap-3 p-3 sm:p-4 hover:bg-[#F8FAFC]" data-testid={`tft-txn-${t.id}`}>
                  <div className="flex items-center gap-3 min-w-0">
                    <div className="w-9 h-9 rounded-full flex items-center justify-center flex-shrink-0" style={{ backgroundColor: `${meta.color}15` }}>
                      <Icon className="w-4 h-4" style={{ color: meta.color }} />
                    </div>
                    <div className="min-w-0">
                      <p className="text-sm font-semibold text-[#0A2540]">{meta.label}</p>
                      <p className="text-xs text-[#64748B] truncate">{t.description || (t.booking_id ? `Commande ${String(t.booking_id).slice(0, 8)}` : "—")}</p>
                      <p className="text-[10px] text-[#94A3B8] mt-0.5">{formatDate(t.created_at)}</p>
                    </div>
                  </div>
                  <div className="text-right flex-shrink-0">
                    <p className="text-sm font-bold" style={{ color: meta.color, fontFamily: 'Outfit' }}>
                      {meta.sign}{(t.amount || 0).toLocaleString()} TFT
                    </p>
                    {typeof t.balance_after === "number" && (
                      <p className="text-[10px] text-[#94A3B8]">Solde: {t.balance_after.toLocaleString()}</p>
                    )}
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </div>
  );
}
